import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminToken, getAdminUser, setAdminToken, setAdminUser, removeAdminToken, removeAdminUser } from '../utils/auth';
import type { AdminUser } from '../types';

export const useAuth = () => {
  // Initialize synchronously from localStorage to avoid guard flicker
  const [user, setUser] = useState<AdminUser | null>(() => getAdminUser());
  const [loading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Keep state in sync if storage changes in other tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'adminUser' || e.key === 'adminToken') {
        const token = getAdminToken();
        const userData = getAdminUser();
        if (token && userData) {
          setUser(userData);
        } else {
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6548';
      const loginUrl = `${apiUrl}/admin/auth/login`; // Use working admin endpoint
      console.log('Login URL:', loginUrl);
      
      // Backend expects email and password, not username
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: username, // Send as username (backend accepts both username and email)
          email: username.includes('@') ? username : undefined, // Also send as email if it contains @
          password: password
        }),
      });

      console.log('Login response status:', response.status);
      const data = await response.json();
      console.log('Login response data:', data);

      if (data.success && (data.token || data.data?.token)) {
        console.log('Setting admin token and user...');
        // Handle different response formats
        const token = data.token || data.data?.token;
        const user = data.user || data.data?.user;
        const redirectUrl = data.redirectUrl || '/admin/dashboard';
        
        setAdminToken(token);
        setAdminUser(user);
        setUser(user);
        console.log('Auth state updated, user:', user);
        return { success: true, redirectUrl };
      } else {
        console.log('Login failed - data structure:', {
          success: data.success,
          hasToken: !!data.token,
          hasDataToken: !!data.data?.token,
          hasUser: !!data.user,
          hasDataUser: !!data.data?.user,
          message: data.message
        });
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed: ' + (error instanceof Error ? error.message : 'Unknown error') };
    }
  };

  const logout = () => {
    removeAdminToken();
    removeAdminUser();
    setUser(null);
    navigate('/admin/login');
  };

  const updateUser = (userData: Partial<AdminUser>) => {
    const updatedUser = { ...user, ...userData } as AdminUser;
    setUser(updatedUser);
    setAdminUser(updatedUser);
  };

  return {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };
};
